import { Plan } from '@/constants/enum';
import { fileToBase64 } from '@/helpers/file';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IParamsApi } from '@/types/apis/params';
import { Button, DropZone, InlineError, Text } from '@shopify/polaris';
import { memo, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

interface IProps {
  setSelectedIcon: React.Dispatch<React.SetStateAction<IParamsApi.IDataSettingTimelineIcon | undefined>>;
  selectedIcon?: IParamsApi.IDataSettingTimelineIcon;
  setHasErrorFile: React.Dispatch<React.SetStateAction<boolean>>;
}

function UploadIconTab({ setSelectedIcon, selectedIcon, setHasErrorFile }: IProps) {
  const dataSettings = useSelector(dataSettingsSelector);
  const [file, setFile] = useState<File | string | null>(
    selectedIcon ? selectedIcon.iconUrl || selectedIcon.iconFile || '' : null,
  );
  const [fileName, setFileName] = useState('');
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (selectedIcon && !(selectedIcon instanceof File)) {
      setFile(selectedIcon.iconUrl || selectedIcon.iconFile || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedIcon)]);

  const validateFile = (file: any) => {
    return file?.size < 1000000;
  };

  const toggleOpenFileDialog = useCallback(() => setOpenFileDialog((openFileDialog) => !openFileDialog), []);

  const handleDropZoneDrop = useCallback(
    async (_dropFiles: File[], acceptedFiles: File[], _rejectedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file || file?.size > 1000000) {
        setFile(null);
        setHasErrorFile(true);
        setError('Invalid file format or size. Try upload a file in .svg, .gif, .jpg, .png and under 1MB.');
        return;
      }
      setHasErrorFile(false);
      setError('');

      setFile(file);
      setFileName(file.name);
      let newFile: File | string = file;
      if (file instanceof File) {
        newFile = (await fileToBase64(file)) as string;
      }
      setSelectedIcon({
        ...selectedIcon,
        iconFile: newFile instanceof File ? '' : newFile,
        iconUrl: newFile instanceof File ? '' : newFile,
      });
    },
    [selectedIcon, setHasErrorFile, setSelectedIcon],
  );

  return (
    <div className="mt-16">
      {file ? (
        <div style={{ padding: 20, border: '1px dashed #ccc', borderRadius: 10 }} className="d-flex content-between">
          <div className="d-flex align-center">
            <img
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                border: '1px solid #4b4b4b',
                objectFit: 'cover',
                objectPosition: 'center',
                marginRight: '1rem',
              }}
              src={file instanceof File ? URL.createObjectURL(file) : file}
              alt="icon"
            />
            <Text as="p" variant="bodyMd" fontWeight="medium">
              {fileName || ''}
            </Text>
          </div>
          <div className="d-flex align-center">
            <Button disabled={dataSettings.plan === Plan.FREE} onClick={toggleOpenFileDialog}>
              Replace
            </Button>
          </div>
        </div>
      ) : null}
      <div style={{ height: file ? 0 : 'auto', overflow: 'hidden' }}>
        <DropZone
          accept=".svg, .gif, .jpg, .png"
          errorOverlayText="File must be in .svg, .gif, .jpg, .png and under 1MB"
          type="image"
          customValidator={validateFile}
          allowMultiple={false}
          onDrop={handleDropZoneDrop}
          openFileDialog={openFileDialog}
          onFileDialogClose={toggleOpenFileDialog}
          disabled={dataSettings.plan === Plan.FREE}
        >
          <div>
            <DropZone.FileUpload actionHint="File must be in .svg, .gif, .jpg, .png and under 1MB" />
          </div>
        </DropZone>
      </div>
      {error ? (
        <div className="mt-4">
          <InlineError fieldID="" message={error} />
        </div>
      ) : null}
    </div>
  );
}

export default memo(UploadIconTab);
